import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Calendar, Receipt, Upload, X, DollarSign, FileText, Building2, ChevronDown, ChevronUp, Camera, Sparkles } from 'lucide-react';
import { expensesApi, expenseCategoriesApi, taxConfigApi } from '../services/api';
import storageService from '../services/storageService';
import { formatCurrency, formatDate } from '../utils/helpers';
import Loading from '../components/Loading';
import Modal from '../components/Modal';

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Category state
  const [groupedCategories, setGroupedCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  
  // Receipt upload state
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [receiptPreview, setReceiptPreview] = useState(null);
  
  // SuddySnap state
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [scanConfidence, setScanConfidence] = useState(null);
  
  // Sort state
  const [sortField, setSortField] = useState('expenseDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  
  // Form state
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    category: '',
    vendor: '',
    expenseDate: new Date().toISOString().split('T')[0],
    paymentMethod: '',
    receiptUrl: '',
    scheduleCLine: null,
    deductiblePercent: 100,
    businessUsePercent: 100,
    isTaxDeductible: true,
    notes: '',
  });

  // Payment methods
  const paymentMethods = [
    'Cash',
    'Credit Card',
    'Debit Card',
    'Check',
    'Bank Transfer',
    'Other'
  ];

  // Load categories on mount
  useEffect(() => {
    loadCategories();
  }, []);

  // Load expenses on mount
  useEffect(() => {
    loadExpenses();
  }, []);

  // Filter and sort expenses
  useEffect(() => {
    let filtered = expenses.filter(expense => {
  const matchesSearch = 
    expense.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    expense.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    expense.vendor?.toLowerCase().includes(searchTerm.toLowerCase());
  const matchesYear = yearFilter === 'all' || 
  parseInt(expense.expenseDate.substring(0, 4)) === parseInt(yearFilter);
  return matchesSearch && matchesYear;
});
    
    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'expenseDate') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else if (sortField === 'amount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
    
    setFilteredExpenses(filtered);
  }, [searchTerm, expenses, sortField, sortDirection, yearFilter]);

  const loadCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await expenseCategoriesApi.getGrouped();
      setGroupedCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const response = await expensesApi.getAll();
      const sorted = response.data.sort((a, b) => 
        new Date(b.expenseDate) - new Date(a.expenseDate)
      );
      setExpenses(sorted);
      setFilteredExpenses(sorted);
    } catch (error) {
      console.error('Error loading expenses:', error);
      alert('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  // Find category details by categoryKey
  const getCategoryDetails = useCallback((categoryKey) => {
    for (const group of groupedCategories) {
      const found = group.categories.find(c => c.categoryKey === categoryKey);
      if (found) return found;
    }
    return null;
  }, [groupedCategories]);

  // Handle category selection - auto-fill tax fields
  const handleCategoryChange = async (categoryKey) => {
    const category = getCategoryDetails(categoryKey);
    
    if (category) {
      let businessUsePercent = category.requiresBusinessUsePercent ? formData.businessUsePercent : 100;
      
      // For vehicle categories, try to get the configured business use % from tax settings
      if (category.requiresBusinessUsePercent && category.groupName === 'Vehicle') {
        try {
          const response = await taxConfigApi.getConfiguration();
          if (response.data?.vehicleBusinessUsePercent) {
            businessUsePercent = response.data.vehicleBusinessUsePercent;
          }
        } catch (error) {
          console.warn('Could not load tax config for vehicle %:', error);
        }
      }
      
      setFormData(prev => ({
        ...prev,
        category: categoryKey,
        scheduleCLine: category.scheduleCLine,
        deductiblePercent: category.defaultDeductiblePercent,
        businessUsePercent: businessUsePercent,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        category: categoryKey,
      }));
    }
  };

  // ============================================
  // SuddySnap™ - AI Receipt Scanner
  // ============================================
  const handleSuddySnap = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset the input so the same file can be selected again
    e.target.value = '';

    // Validate file size
    if (file.size > 10 * 1024 * 1024) {
      setScanError('File is too large. Maximum size is 10MB.');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/heic', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setScanError('Invalid file type. Please use JPG, PNG, HEIC, or PDF.');
      return;
    }

    try {
      setScanning(true);
      setScanError(null);
      setScanConfidence(null);
      
      // Don't set preview yet - keep the SuddySnap box visible with spinner
      // Preview will be set after successful scan

      // Send to SuddySnap API
      const response = await expensesApi.scanReceipt(file);
      const data = response.data;

      // Store confidence for display
      if (data.confidence) {
        setScanConfidence(Math.round(data.confidence * 100));
      }

      // Auto-fill form with extracted data
      const updates = {
        receiptUrl: data.receiptUrl || formData.receiptUrl,
      };

      if (data.vendor) {
        updates.vendor = data.vendor;
      }
      if (data.amount) {
        updates.amount = data.amount.toFixed(2);
      }
      if (data.transactionDate) {
        updates.expenseDate = new Date(data.transactionDate).toISOString().split('T')[0];
      }

      setFormData(prev => ({ ...prev, ...updates }));

      // If category was suggested, apply it (which also sets tax defaults)
      if (data.suggestedCategory) {
        await handleCategoryChange(data.suggestedCategory);
        // Re-apply the extracted values since handleCategoryChange might reset some
        setFormData(prev => ({
          ...prev,
          vendor: data.vendor || prev.vendor,
          amount: data.amount ? data.amount.toFixed(2) : prev.amount,
          expenseDate: data.transactionDate 
            ? new Date(data.transactionDate).toISOString().split('T')[0] 
            : prev.expenseDate,
          receiptUrl: data.receiptUrl || prev.receiptUrl,
        }));
      }

      // Auto-generate description if we have vendor and line items
      if (!formData.description && data.vendor) {
        const desc = data.lineItems?.length > 0 
          ? `${data.vendor} - ${data.lineItems[0].description || 'Purchase'}`.substring(0, 200)
          : `${data.vendor} purchase`;
        setFormData(prev => ({ ...prev, description: prev.description || desc }));
      }

      // NOW show the receipt preview (after scan completes successfully)
      if (file.type === 'application/pdf') {
        setReceiptPreview('pdf');
      } else {
        // For images, read the file locally for preview (blob URL requires SAS token)
        const reader = new FileReader();
        reader.onload = (e) => setReceiptPreview(e.target?.result);
        reader.readAsDataURL(file);
      }

    } catch (error) {
      console.error('SuddySnap error:', error);
      setScanError(
        error.response?.data?.message || 
        'Could not read receipt. Please enter details manually.'
      );
      // Still show preview on error so user can manually enter data
      if (file.type === 'application/pdf') {
        setReceiptPreview('pdf');
      } else {
        const reader = new FileReader();
        reader.onload = (e) => setReceiptPreview(e.target?.result);
        reader.readAsDataURL(file);
      }
    } finally {
      setScanning(false);
    }
  };

  // Handle receipt file selection (manual upload without OCR)
  const handleReceiptSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset the input
    e.target.value = '';

    if (file.size > 10 * 1024 * 1024) {
      alert('File is too large. Maximum size is 10MB.');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/heic', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Please upload a JPG, PNG, HEIC, or PDF file.');
      return;
    }

    try {
      setUploadingReceipt(true);
      
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setReceiptPreview(e.target?.result);
        reader.readAsDataURL(file);
      } else {
        setReceiptPreview('pdf');
      }

      const blobUrl = await storageService.uploadReceipt(file);
      
      setFormData(prev => ({
        ...prev,
        receiptUrl: blobUrl,
      }));
    } catch (error) {
      console.error('Error uploading receipt:', error);
      alert('Failed to upload receipt. Please try again.');
      setReceiptPreview(null);
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleRemoveReceipt = async () => {
    if (formData.receiptUrl && !editingExpense?.receiptUrl) {
      try {
        await storageService.deleteReceipt(formData.receiptUrl);
      } catch (error) {
        console.error('Error deleting receipt:', error);
      }
    }
    
    setFormData(prev => ({ ...prev, receiptUrl: '' }));
    setReceiptPreview(null);
    setScanConfidence(null);
    setScanError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount) || 0,
        deductiblePercent: parseFloat(formData.deductiblePercent) || 100,
        businessUsePercent: parseFloat(formData.businessUsePercent) || 100,
      };

      if (editingExpense) {
        await expensesApi.update(editingExpense.id, submitData);
      } else {
        await expensesApi.create(submitData);
      }
      
      await loadExpenses();
      handleCloseModal(true);
    } catch (error) {
      console.error('Error saving expense:', error);
      alert('Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      description: expense.description || '',
      amount: expense.amount ? expense.amount.toFixed(2) : '',
      category: expense.category || '',
      vendor: expense.vendor || '',
      expenseDate: expense.expenseDate?.split('T')[0] || '',
      paymentMethod: expense.paymentMethod || '',
      receiptUrl: expense.receiptUrl || '',
      scheduleCLine: expense.scheduleCLine || null,
      deductiblePercent: expense.deductiblePercent ?? 100,
      businessUsePercent: expense.businessUsePercent ?? 100,
      isTaxDeductible: expense.isTaxDeductible ?? true,
      notes: expense.notes || '',
    });
    
    if (expense.receiptUrl) {
      setReceiptPreview(expense.receiptUrl.toLowerCase().endsWith('.pdf') ? 'pdf' : expense.receiptUrl);
    }
    
    setScanError(null);
    setScanConfidence(null);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) {
      return;
    }

    try {
      await expensesApi.delete(id);
      await loadExpenses();
    } catch (error) {
      console.error('Error deleting expense:', error);
      alert('Failed to delete expense');
    }
  };

  const handleCloseModal = async (wasSaved = false) => {
  // Clean up uploaded receipt if user cancels without saving
  // Only delete if it's a NEW upload (not an existing receipt being edited)
  if (!wasSaved && formData.receiptUrl && (!editingExpense || formData.receiptUrl !== editingExpense.receiptUrl)) {
    try {
      await storageService.deleteReceipt(formData.receiptUrl);
    } catch (error) {
      console.error('Error cleaning up receipt on cancel:', error);
      // Don't block the close if cleanup fails
    }
  }
    
    setShowModal(false);
    setEditingExpense(null);
    setReceiptPreview(null);
    setScanError(null);
    setScanConfidence(null);
    setFormData({
      description: '',
      amount: '',
      category: '',
      vendor: '',
      expenseDate: new Date().toISOString().split('T')[0],
      paymentMethod: '',
      receiptUrl: '',
      scheduleCLine: null,
      deductiblePercent: 100,
      businessUsePercent: 100,
      isTaxDeductible: true,
      notes: '',
    });
  };

  const getCategoryDisplayName = (categoryKey) => {
    const category = getCategoryDetails(categoryKey);
    return category?.displayName || categoryKey;
  };

  const selectedCategoryRequiresBusinessUse = () => {
    const category = getCategoryDetails(formData.category);
    return category?.requiresBusinessUsePercent || false;
  };

  const calculateDeductible = () => {
    const amount = parseFloat(formData.amount) || 0;
    const deductPct = parseFloat(formData.deductiblePercent) || 100;
    const businessPct = parseFloat(formData.businessUsePercent) || 100;
    
    if (!formData.isTaxDeductible) return 0;
    return amount * (deductPct / 100) * (businessPct / 100);
  };
  
  // Handle sort toggle
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  // Sort indicator component
  const SortIndicator = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  // View receipt helper
  const handleViewReceipt = async (receiptUrl, e) => {
    e?.stopPropagation();
    try {
      const readUrl = await storageService.getReadUrl(receiptUrl);
      window.open(readUrl, '_blank');
    } catch (error) {
      console.error('Error getting receipt URL:', error);
      alert('Failed to load receipt');
    }
  };

  if (loading) return <Loading fullScreen />;

const totalExpenses = (filteredExpenses || []).reduce((sum, exp) => sum + (exp.amount || 0), 0);
const totalDeductible = (filteredExpenses || []).reduce((sum, exp) => sum + (exp.deductibleAmount || 0), 0);

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      {/* Compact Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Expenses</h1>
          <p className="text-sm text-gray-600">
            Total: <span className="font-semibold">{formatCurrency(totalExpenses)}</span>
            <span className="mx-2">•</span>
            <span className="text-green-600">Deductible: <span className="font-semibold">{formatCurrency(totalDeductible)}</span></span>
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn btn-primary flex items-center gap-2 justify-center whitespace-nowrap"
        >
          <Plus size={18} />
          Add Expense
        </button>
      </div>

      {/* Search + Year Filter */}
<div className="mb-4 flex gap-2">
  <div className="relative flex-1">
    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
    <input
      type="text"
      placeholder="Search by description, category, or vendor..."
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      className="input pl-9 py-2 text-sm"
    />
  </div>
  <select
    value={yearFilter}
    onChange={(e) => setYearFilter(e.target.value)}
    className="input py-2 text-sm w-28 flex-shrink-0"
  >
    {Array.from(
      new Set(expenses.map(e => new Date(e.expenseDate).getFullYear()))
    ).sort((a, b) => b - a).map(year => (
      <option key={year} value={year}>{year}</option>
    ))}
    <option value="all">All Years</option>
  </select>
</div>

      {/* Expenses List */}
      {filteredExpenses.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-500">
            {searchTerm ? 'No expenses found' : 'No expenses yet. Add your first expense!'}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 text-left text-sm text-gray-600">
                  <th 
                    className="pb-2 pr-4 font-medium cursor-pointer hover:text-gray-900"
                    onClick={() => handleSort('expenseDate')}
                  >
                    <span className="flex items-center gap-1">
                      Date <SortIndicator field="expenseDate" />
                    </span>
                  </th>
                  <th className="pb-2 pr-4 font-medium">Category</th>
                  <th className="pb-2 pr-4 font-medium">Vendor</th>
                  <th 
                    className="pb-2 pr-4 font-medium cursor-pointer hover:text-gray-900"
                    onClick={() => handleSort('description')}
                  >
                    <span className="flex items-center gap-1">
                      Description <SortIndicator field="description" />
                    </span>
                  </th>
                  <th 
                    className="pb-2 pr-4 font-medium text-right cursor-pointer hover:text-gray-900"
                    onClick={() => handleSort('amount')}
                  >
                    <span className="flex items-center justify-end gap-1">
                      Amount <SortIndicator field="amount" />
                    </span>
                  </th>
                  <th className="pb-2 pr-4 font-medium text-center">Receipt</th>
                  <th className="pb-2 pr-4 font-medium text-center">Deductible</th>
                  <th className="pb-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredExpenses.map((expense) => (
                  <tr key={expense.id} className="hover:bg-gray-50 text-sm">
                    <td className="py-2.5 pr-4 text-gray-600 whitespace-nowrap">
                      {formatDate(expense.expenseDate)}
                    </td>
                    <td className="py-2.5 pr-4">
                      <span className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded truncate max-w-[180px]">
                        {getCategoryDisplayName(expense.category)}
                      </span>
                    </td>
                    <td className="py-2.5 pr-4 text-gray-700 truncate max-w-[120px]">
                      {expense.vendor || '-'}
                    </td>
                    <td className="py-2.5 pr-4 text-gray-900 truncate max-w-[200px]" title={expense.description}>
                      {expense.description}
                    </td>
                    <td className="py-2.5 pr-4 text-right font-medium text-red-600 whitespace-nowrap">
                      {formatCurrency(expense.amount)}
                    </td>
                    <td className="py-2.5 pr-4 text-center">
                      {expense.receiptUrl ? (
                        <button
                          onClick={(e) => handleViewReceipt(expense.receiptUrl, e)}
                          className="text-blue-600 hover:text-blue-700 hover:underline text-xs inline-flex items-center gap-1"
                        >
                          <Receipt size={12} />
                          View
                        </button>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 text-center">
                      {expense.isTaxDeductible ? (
                        <span className="text-green-600 text-xs">
                          ✓ {expense.deductiblePercent || 100}%
                        </span>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="py-2.5 text-right whitespace-nowrap">
                      <button
                        onClick={() => handleEdit(expense)}
                        className="text-blue-600 hover:text-blue-700 text-xs font-medium mr-3"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(expense.id)}
                        className="text-red-600 hover:text-red-700 text-xs font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View - Compact */}
          <div className="lg:hidden space-y-2">
            {filteredExpenses.map((expense) => (
              <div 
                key={expense.id} 
                className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-medium text-gray-900 text-sm truncate pr-2">
                        {expense.description}
                      </h3>
                      <span className="font-semibold text-red-600 text-sm whitespace-nowrap">
                        {formatCurrency(expense.amount)}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                      <span className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 truncate max-w-[140px]">
                        {getCategoryDisplayName(expense.category)}
                      </span>
                      {expense.vendor && (
                        <span className="truncate max-w-[100px]">• {expense.vendor}</span>
                      )}
                      <span>• {formatDate(expense.expenseDate)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                  <div className="flex items-center gap-3 text-xs">
                    {expense.receiptUrl && (
                      <button
                        onClick={(e) => handleViewReceipt(expense.receiptUrl, e)}
                        className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <Receipt size={12} />
                        Receipt
                      </button>
                    )}
                    {expense.isTaxDeductible && (
                      <span className="text-green-600">✓ {expense.deductiblePercent || 100}%</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleEdit(expense)}
                      className="text-blue-600 hover:text-blue-700 text-xs font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(expense.id)}
                      className="text-red-600 hover:text-red-700 text-xs font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingExpense ? 'Edit Expense' : 'Add New Expense'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          
          {/* ============================================
              SuddySnap™ Receipt Scanner Section
              Show when: Adding new OR editing without receipt, AND no preview yet OR currently scanning
              ============================================ */}
          {(!editingExpense || !editingExpense.receiptUrl) && (!receiptPreview || scanning) && (
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={18} className="text-blue-600" />
                <span className="font-semibold text-blue-900">SuddySnap™</span>
                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">AI-Powered</span>
              </div>
              
              {/* Mobile: Show camera option */}
              <p className="text-sm text-blue-700 mb-3 sm:hidden">
                Snap a receipt photo and let AI fill in the details automatically.
              </p>
              
              {/* Desktop: Different messaging */}
              <p className="text-sm text-blue-700 mb-3 hidden sm:block">
                Upload a receipt image and let AI fill in the details automatically.
              </p>
              
              <div className="flex gap-2">
                {/* SuddySnap Button - Camera (Mobile Only) */}
                <div className="flex-1 sm:hidden">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleSuddySnap}
                    className="hidden"
                    id="suddy-snap-camera"
                    disabled={scanning || uploadingReceipt}
                  />
                  <label
                    htmlFor="suddy-snap-camera"
                    className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium cursor-pointer transition-all ${
                      scanning 
                        ? 'bg-blue-400 text-white cursor-wait' 
                        : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
                    }`}
                  >
                    {scanning ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        <span>Reading...</span>
                      </>
                    ) : (
                      <>
                        <Camera size={18} />
                        <span>Snap Receipt</span>
                      </>
                    )}
                  </label>
                </div>
                
                {/* SuddySnap Button - File Upload (Both Mobile & Desktop) */}
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleSuddySnap}
                    className="hidden"
                    id="suddy-snap-file"
                    disabled={scanning || uploadingReceipt}
                  />
                  <label
                    htmlFor="suddy-snap-file"
                    className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium cursor-pointer transition-all ${
                      scanning 
                        ? 'bg-blue-400 text-white cursor-wait sm:bg-blue-400' 
                        : 'bg-white text-blue-600 border border-blue-300 hover:bg-blue-50 sm:bg-blue-600 sm:text-white sm:border-blue-600 sm:hover:bg-blue-700'
                    }`}
                  >
                    {scanning ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent sm:border-white"></div>
                        <span className="hidden sm:inline">Reading receipt...</span>
                        <span className="sm:hidden">Reading...</span>
                      </>
                    ) : (
                      <>
                        <Upload size={18} />
                        <span className="hidden sm:inline">Upload Receipt</span>
                        <span className="sm:hidden">Choose File</span>
                      </>
                    )}
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Receipt Preview (shown after scan or manual upload) */}
          {receiptPreview && !scanning && (
            <div className="relative border rounded-lg p-3 bg-gray-50">
              <button
                type="button"
                onClick={handleRemoveReceipt}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 z-10"
              >
                <X size={14} />
              </button>
              {receiptPreview === 'pdf' ? (
                <div className="flex items-center gap-3">
                  <FileText className="h-8 w-8 text-red-500" />
                  <span className="text-sm text-gray-600">PDF Receipt Attached</span>
                </div>
              ) : (
                <img
                  src={receiptPreview}
                  alt="Receipt preview"
                  className="max-h-32 mx-auto rounded"
                />
              )}
            </div>
          )}

          {/* Scan Status Messages - Always visible when applicable */}
          {scanConfidence !== null && !scanError && (
            <div className="p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">
              ✓ Receipt scanned! Confidence: {scanConfidence}% — Review and adjust below.
            </div>
          )}
          {scanError && (
            <div className="p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
              ⚠️ {scanError}
            </div>
          )}

          {/* Vendor & Description */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Vendor</label>
              <input
                type="text"
                value={formData.vendor}
                onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                className="input"
                placeholder="e.g., Home Depot, Shell"
              />
            </div>
            <div>
              <label className="label">Description *</label>
              <input
                type="text"
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input"
                placeholder="What did you buy?"
              />
            </div>
          </div>

          {/* Amount, Date, Payment Method */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="label">Amount *</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="input pl-8"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div>
              <label className="label">Date *</label>
              <input
                type="date"
                required
                value={formData.expenseDate}
                onChange={(e) => setFormData({ ...formData, expenseDate: e.target.value })}
                className="input"
              />
            </div>
            <div>
              <label className="label">Payment Method</label>
              <select
                value={formData.paymentMethod}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                className="input"
              >
                <option value="">Select...</option>
                {paymentMethods.map((method) => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="label">Category *</label>
            {categoriesLoading ? (
              <div className="input bg-gray-50 text-gray-500">Loading categories...</div>
            ) : (
              <select
                required
                value={formData.category}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="input"
              >
                <option value="">Select category</option>
                {groupedCategories.map((group) => (
                  <optgroup key={group.groupName} label={group.groupName}>
                    {group.categories.map((cat) => (
                      <option key={cat.categoryKey} value={cat.categoryKey}>
                        {cat.displayName}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            )}
            {formData.scheduleCLine && (
              <p className="text-xs text-blue-600 mt-1">
                Schedule C Line {formData.scheduleCLine}
              </p>
            )}
          </div>

          {/* Tax Deductibility Section */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-700">Tax Deductibility</span>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isTaxDeductible}
                  onChange={(e) => setFormData({ ...formData, isTaxDeductible: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-sm text-gray-600">Tax Deductible</span>
              </label>
            </div>

            {formData.isTaxDeductible && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label text-sm">Deductible %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.deductiblePercent}
                    onChange={(e) => setFormData({ ...formData, deductiblePercent: e.target.value })}
                    className="input text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Usually 100%, meals are 50%
                  </p>
                </div>
                
                {selectedCategoryRequiresBusinessUse() && (
                  <div>
                    <label className="label text-sm">Business Use %</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.businessUsePercent}
                      onChange={(e) => setFormData({ ...formData, businessUsePercent: e.target.value })}
                      className="input text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      For mixed personal/business use
                    </p>
                  </div>
                )}
              </div>
            )}

            {formData.amount && (
              <div className="pt-2 border-t border-gray-200">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Deductible Amount:</span>
                  <span className="font-semibold text-green-600">
                    {formatCurrency(calculateDeductible())}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Manual Receipt Upload (for editing or if SuddySnap wasn't used) */}
          {editingExpense && !receiptPreview && (
            <div>
              <label className="label">Receipt</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleReceiptSelect}
                  className="hidden"
                  id="receipt-upload"
                  disabled={uploadingReceipt}
                />
                <label
                  htmlFor="receipt-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  {uploadingReceipt ? (
                    <>
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-2"></div>
                      <span className="text-sm text-gray-500">Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="h-6 w-6 text-gray-400 mb-1" />
                      <span className="text-sm text-gray-600">Click to upload receipt</span>
                      <span className="text-xs text-gray-400">JPG, PNG, HEIC, or PDF (max 10MB)</span>
                    </>
                  )}
                </label>
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="label">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="input"
              rows="2"
              placeholder="Any additional details..."
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4">
            <button 
              type="submit" 
              className="btn btn-primary flex-1"
              disabled={saving || uploadingReceipt || scanning}
            >
              {saving ? 'Saving...' : editingExpense ? 'Update Expense' : 'Add Expense'}
            </button>
            <button
              type="button"
              onClick={handleCloseModal}
              className="btn btn-secondary flex-1"
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Expenses;